import { PublicShell } from "@/components/public/public-shell";
import { btn } from "@/components/ds/styles";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";
import { Clock, MessageCircle, ShieldCheck } from "lucide-react";
import { smartReportDate } from "@/lib/bousala/smart-order";
import { getSmartPortalState, getSmartUiProfile, type SmartOrderSummary } from "@/lib/bousala/smart-portal.functions";
import { getSmartIntakeContext, submitSmartIntake } from "@/lib/bousala/smart-intake.functions";
import { getBousalaResumeState } from "@/lib/bousala-purchase.functions";
import { SmartIntakeForm } from "@/components/bousala/smart/smart-intake-form";
import { SmartReportView } from "@/components/bousala/smart/smart-report-view";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { arabicDateTimeDamascus } from "@/lib/arabic-format";
import {
  SMART_REPORT_LANGUAGE_LABELS,
  SMART_INTAKE_LABELS_AR,
  SMART_INTAKE_LABELS_EN,
  smartCountryLabel,
  smartFinancialLabel,
} from "@/lib/bousala/smart-intake-schema";
import { toWhatsAppLink } from "@/lib/whatsapp-link";
import { useSmartAdminPreview, SmartPreviewBanner } from "@/components/bousala/smart/smart-preview";
import { SmartLanguageToggle, useSmartUiLanguage } from "@/components/bousala/smart/smart-language-toggle";
import { SMART_PORTAL_COPY } from "@/lib/bousala/smart-buyer-copy";
import { userErrorMessage } from "@/lib/user-error";
import type { Lang } from "@/lib/bousala/i18n";

const WHATSAPP = "963958645116";

export const Route = createFileRoute("/_authenticated/portal/smart")({
  head: () => ({
    meta: [
      { title: "البوصلة الذكية — حسابي" },
      { name: "robots", content: "noindex" },
    ],
  }),
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: () => (
    <PublicShell ground="bg-cream-200">
      <SmartPortalPage />
    </PublicShell>
  ),
});

function Shell({ children, lang, onLanguageChange }: { children: React.ReactNode; lang: Lang; onLanguageChange: (lang: Lang) => void }) {
  const { isPreview } = useSmartAdminPreview(null);
  return (
    <div dir={lang === "en" ? "ltr" : "rtl"} className="mx-auto w-full max-w-[44rem] px-5 py-10">
      <div className="flex items-center justify-between gap-3">
        <p className="font-display text-[14px] font-bold text-amber-800">{SMART_PORTAL_COPY[lang].smartName}</p>
        <SmartLanguageToggle lang={lang} onChange={onLanguageChange} />
      </div>
      <div className="mt-4"><SmartPreviewBanner show={isPreview} lang={lang} /></div>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function Panel({
  title,
  body,
  children,
}: {
  title: string;
  body?: string;
  children?: React.ReactNode;
}) {
  return (
    <section>
      <h1 className="font-display text-[34px] font-extrabold leading-[1.3] text-brown-700">{title}</h1>
      {body && <p className="mt-3 text-[17px] leading-[1.85] text-brown-700">{body}</p>}
      {children && <div className="mt-6">{children}</div>}
    </section>
  );
}

function WhatsAppButton({ text, lang }: { text: string; lang: Lang }) {
  return (
    <a
      href={toWhatsAppLink(WHATSAPP, text)}
      target="_blank"
      rel="noopener noreferrer"
      className={`${btn.primary} min-h-14`}
    >
      <MessageCircle className="size-5" strokeWidth={1.75} aria-hidden />
      {SMART_PORTAL_COPY[lang].whatsapp}
    </a>
  );
}

function SmartPortalPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["smart", "portal"],
    queryFn: () => getSmartPortalState(),
  });
  const profile = useQuery({ queryKey: ["smart", "ui-profile"], queryFn: () => getSmartUiProfile(), staleTime: 60_000 });
  const [lang, setLang] = useSmartUiLanguage(profile.data?.preferredLanguage);
  const copy = SMART_PORTAL_COPY[lang];
  const shell = (children: React.ReactNode) => <Shell lang={lang} onLanguageChange={setLang}>{children}</Shell>;

  if (isLoading || !data) {
    return shell(<div role="status" aria-label={copy.loading} className="space-y-3"><div className="h-9 w-2/3 animate-pulse rounded bg-cream-100" /><div className="h-5 w-full animate-pulse rounded bg-cream-100" /></div>);
  }

  switch (data.kind) {
    case "transfer_pending":
      return shell(<PendingPanel lang={lang} />);
    case "transfer_rejected":
      return shell(
          <Panel
            title={copy.rejectedTitle}
            body={copy.rejectedBody}
          >
            <WhatsAppButton text={copy.rejectedMessage} lang={lang} />
          </Panel>
      );
    case "none":
      return shell(
          <Panel
            title={copy.noOrderTitle}
            body={copy.noOrderBody}
          >
            <Link
              to="/bousala/smart"
              className={`${btn.primary} min-h-14`}
            >
              {copy.seeSmart}
            </Link>
          </Panel>
      );
    case "awaiting_assessment":
      return shell(
          <Panel
            title={copy.assessmentTitle}
            body={copy.assessmentBody}
          >
            <Link
              to="/bousala/play"
              className={`${btn.primary} min-h-14`}
            >
              {copy.continueAssessment}
            </Link>
          </Panel>
      );
    case "closed":
      return shell(
          <Panel
            title={data.status === "refunded" ? copy.refundedTitle : copy.stoppedTitle}
            body={
              data.status === "refunded"
                ? copy.refundedBody
                : copy.stoppedBody
            }
          >
            <WhatsAppButton text={copy.orderQuestion} lang={lang} />
          </Panel>
      );
    case "preparing":
      return shell(<PreparingPanel slaDueAt={data.slaDueAt} summary={data.summary} lang={lang} />);
    case "intake":
      return shell(<IntakePanel lang={lang} />);
    case "report":
      return (
        <div
          dir={data.header.reportLanguage === "en" ? "ltr" : "rtl"}
          className="w-full px-5 py-10"
        >
          <SmartReportView
            header={{
              clientName: data.header.firstName || (data.header.reportLanguage === "en" ? "Student" : "طالبنا"),
              code: data.header.code || "—",
              deliveredAt: smartReportDate(data.header.sentAt, data.header.reportLanguage),
              consideredMajors: data.header.majors,
              targetCountry: data.header.targetCountry ? smartCountryLabel(data.header.targetCountry, data.header.reportLanguage) : "—",
            }}
            sections={data.sections}
            lang={data.header.reportLanguage}
          />
        </div>
      );
  }
}

/** Bousala 1 state for the buyer, used for the "start the test now" CTAs. */
function useBousalaResume() {
  return useQuery({
    queryKey: ["bousala", "resume-state", "smart-portal"],
    queryFn: () => getBousalaResumeState(),
    staleTime: 30_000,
    retry: false,
  });
}

function FreeReportLink({ attemptId, lang }: { attemptId: string | null | undefined; lang: Lang }) {
  if (!attemptId) return null;
  return (
    <Link
      to="/portal/bousala/$attemptId"
      params={{ attemptId }}
      className="mt-4 inline-flex min-h-12 items-center font-display text-[15px] font-bold text-amber-800 underline underline-offset-4"
    >
      {SMART_PORTAL_COPY[lang].freeReport}
    </Link>
  );
}

/** Payment under review: the buyer can (and should) start the free Bousala now. */
function PendingPanel({ lang }: { lang: Lang }) {
  const copy = SMART_PORTAL_COPY[lang];
  const { data } = useBousalaResume();
  const status = data?.attemptStatus ?? null;
  const done = status === "scored" || status === "report_ready" || status === "completed";
  const started = Boolean(data?.attemptId) && !done;

  return (
    <Panel
      title={copy.pendingTitle}
      body={copy.pendingBody}
    >
      {done ? (
        <div>
          <p className="rounded-ds-lg bg-blue-100 px-5 py-4 text-[17px] leading-[1.85] text-brown-700">
            {copy.resultReady}
          </p>
          <FreeReportLink attemptId={data?.attemptId} lang={lang} />
        </div>
      ) : (
        <div>
          <p className="text-[17px] leading-[1.85] text-brown-700">
            {copy.pendingStartBody}
          </p>
          <Link
            to="/bousala/play"
            className={"mt-4 " + `${btn.primary} min-h-14`}
          >
            {started ? copy.continueBousala : copy.startBousala}
          </Link>
        </div>
      )}
    </Panel>
  );
}

function PreparingPanel({
  slaDueAt,
  summary,
  lang,
}: {
  slaDueAt: string | null;
  summary: SmartOrderSummary;
  lang: Lang;
}) {
  const copy = SMART_PORTAL_COPY[lang];
  const labels = lang === "en" ? SMART_INTAKE_LABELS_EN : SMART_INTAKE_LABELS_AR;
  const rows: Array<[string, string]> = [
    [
      labels.report_language,
      SMART_REPORT_LANGUAGE_LABELS[summary.reportLanguage],
    ],
    [labels.considered_major_ids, summary.majors.join(lang === "en" ? ", " : "، ") || "—"],
    [
      labels.target_country,
      summary.targetCountry ? smartCountryLabel(summary.targetCountry, lang) : "—",
    ],
    [
      labels.financial_context,
      summary.financialContext ? smartFinancialLabel(summary.financialContext, lang) : "—",
    ],
    [labels.family_expectations, summary.familyExpectations || "—"],
    [labels.future_goals, summary.futureGoals || "—"],
    [labels.concerns, summary.concerns || "—"],
  ];

  return (
    <div className="space-y-6">
      <Panel
        title={copy.preparingTitle}
        body={copy.preparingBody}
      >
        {slaDueAt && (
          <p className="inline-flex items-center gap-2 rounded-ds-lg bg-cream-100 px-4 py-3 text-[15px] font-bold text-brown-700">
            <Clock className="size-5 text-amber-800" strokeWidth={1.75} aria-hidden />
            {copy.deliveryDue} {lang === "en" ? new Intl.DateTimeFormat("en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Damascus" }).format(new Date(slaDueAt)) : arabicDateTimeDamascus(slaDueAt)} {copy.damascusTime}
          </p>
        )}
      </Panel>

      <section>
        <h2 className="font-display text-[23px] font-extrabold text-brown-700">{copy.sentSummary}</h2>
        <dl className="mt-3 divide-y divide-hairline-subtle">
          {rows.map(([label, value]) => (
            <div key={label} className="py-3">
              <dt className="text-[13px] text-brown-500">{label}</dt>
              <dd className="mt-1 whitespace-pre-wrap text-[17px] leading-[1.85] text-brown-700">{value}</dd>
            </div>
          ))}
        </dl>
      </section>
    </div>
  );
}

function IntakePanel({ lang }: { lang: Lang }) {
  const copy = SMART_PORTAL_COPY[lang];
  const qc = useQueryClient();
  const submit = useServerFn(submitSmartIntake);
  const [busy, setBusy] = useState(false);
  const { data, isLoading } = useQuery({
    queryKey: ["smart", "intake-context"],
    queryFn: () => getSmartIntakeContext(),
  });

  if (isLoading || !data) {
    return (
      <div role="status" aria-label={copy.loading} className="space-y-3"><div className="h-9 w-2/3 animate-pulse rounded bg-cream-100" /><div className="h-12 animate-pulse rounded-ds-md bg-cream-100" /></div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-[34px] font-extrabold leading-[1.3] text-brown-700">{copy.intakeTitle}</h1>
        <p className="mt-3 text-[17px] leading-[1.85] text-brown-700">
          {copy.intakeBody}
        </p>
      </div>

      <SmartIntakeForm
        majors={data.majors}
        lang={lang}
        languageHints={{ preferredLanguage: lang }}
        initialValue={
          data.existing
            ? {
                report_language: (data.existing.report_language ?? undefined) as any,
                considered_major_ids: data.existing.considered_major_ids,
                target_country: data.existing.target_country ?? undefined,
                financial_context: data.existing.financial_context as any,
                family_expectations: data.existing.family_expectations ?? undefined,
                future_goals: data.existing.future_goals ?? undefined,
                concerns: data.existing.concerns ?? undefined,
              }
            : undefined
        }
        submitting={busy}
        onSubmit={async (value) => {
          if (busy) return;
          setBusy(true);
          try {
            await submit({ data: value });
            toast.success(copy.intakeSuccess);
            qc.invalidateQueries({ queryKey: ["smart"] });
          } catch (e: any) {
            toast.error(userErrorMessage(e, lang, copy.intakeFailed));
          } finally {
            setBusy(false);
          }
        }}
      />

      <IntakeFreeReportLink lang={lang} />

      <p className="flex items-start gap-3 rounded-ds-lg bg-green-100 px-5 py-4 text-[14px] leading-[1.7] text-brown-700">
        <ShieldCheck className="mt-0.5 size-5 shrink-0 text-green-800" strokeWidth={1.75} aria-hidden />
        {copy.privacy}
      </p>
    </div>
  );
}

function IntakeFreeReportLink({ lang }: { lang: Lang }) {
  const { data } = useBousalaResume();
  return <FreeReportLink attemptId={data?.attemptId} lang={lang} />;
}
